// AHA-47349-presenter-can-create-new-slide-type
// Developer slide types for the audience: fetch the slide types the presentation
// CREATOR has made from the backend's public user-manifests endpoint (keyed by
// the creator's userId — the audience may be a guest) and normalise them into the
// audience marketplace catalogue shape, with URLs absolutized against each entry's
// baseUrl so SlidePluginContent can render the iframe. Keep in sync with the
// presenter app's copy.
// Relative import (not the `@/` alias): newly-added files are strict-linted with
// `import/no-unresolved`, and this repo's ESLint has no alias resolver configured.
import Config from '../config'

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '')

// Bounded fetch so a slow/unreachable backend can never stall the loader
// indefinitely. Abort each request after a hard timeout; on abort fetch rejects
// and the caller falls back to [].
const FETCH_TIMEOUT_MS = 5000
const fetchWithTimeout = async (url, options) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null
  try {
    return await fetch(url, controller ? { ...options, signal: controller.signal } : options)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// Make a (possibly relative) URL absolute against the slide type's own baseUrl.
const absolutize = (base, url) => {
  const value = String(url || '')
  if (!value) return value
  if (/^https?:\/\//i.test(value)) return value
  return `${trimTrailingSlash(base)}${value.startsWith('/') ? '' : '/'}${value}`
}

// Normalise one user-manifest slide type into the SAME shape as an /api/slide-types
// catalogue entry so the audience treats it exactly like a first-party plugin. The
// API entry mirrors the catalogue feed: a nested `ahaConfig` (URLs already absolute)
// plus the top-level entry (URLs relative). Flatten it like the marketplace loader —
// spread `ahaConfig`, then the top-level entry so its keys win — then force the
// canonical fields. Key points:
//   • `type` IS the `pageId` — the stable per-deploy identifier a slide stores as
//     `slide.type` (never `marketplace/`-prefixed).
//   • ONLY the plugin's own iframe entries (canvas/editor/audience/handler) plus a
//     path-shaped `icon` are absolutized against `baseUrl`. A FontAwesome-class
//     `icon` (`fas fa-heart`, no `/`) and `previewUrl` (a presenter asset) are kept.
//   • FAIL CLOSED: without an absolute base the audience iframe URL stays relative
//     and would resolve against the audience origin. Drop such entries.
//   • `plugin: true`; `setting` and the rest of the catalogue fields kept from the flatten.
// Keep in sync with the presenter app's copy.
const isAbsoluteUrl = (url) => /^https?:\/\//i.test(String(url || ''))
const normalize = (entry) => {
  if (!entry || typeof entry.pageId !== 'string' || !entry.pageId) return null
  const cfg = (entry.ahaConfig && typeof entry.ahaConfig === 'object') ? entry.ahaConfig : {}
  const { ahaConfig, ...topLevel } = entry
  const merged = { ...cfg, ...topLevel }
  const abs = (url) => absolutize(entry.baseUrl || '', url)
  const audienceUrl = abs(merged.audienceUrl)
  if (!isAbsoluteUrl(audienceUrl)) return null
  const icon = (typeof merged.icon === 'string' && merged.icon.includes('/')) ? abs(merged.icon) : merged.icon
  return {
    ...merged,
    type: entry.pageId,
    slug: entry.slug || cfg.slug || '',
    plugin: true,
    isDeveloperSlideType: true,
    canvasUrl: abs(merged.canvasUrl),
    editorUrl: abs(merged.editorUrl),
    audienceUrl,
    handlerUrl: merged.handlerUrl ? abs(merged.handlerUrl) : (merged.handlerUrl ?? null),
    icon,
    setting: (merged.setting && typeof merged.setting === 'object') ? merged.setting : {},
    baseUrl: entry.baseUrl || '',
    pageId: entry.pageId,
  }
}

/**
 * Returns the creator's developer slide types, or [] when there is nothing to
 * add / the request fails. Read from the backend's public endpoint (no auth —
 * the userId is in the path), so a guest audience can call it.
 */
export const fetchDeveloperSlideTypes = async ({ userId }) => {
  if (!userId) return []
  let data
  try {
    const url = `${trimTrailingSlash(Config.baseUrl)}/api/slide/user-manifests/public/${encodeURIComponent(userId)}`
    const res = await fetchWithTimeout(url, { headers: { accept: 'application/json' } })
    if (!res.ok) return []
    data = await res.json()
  } catch (e) {
    return []
  }
  // Guard a non-array `slideTypes` (object/string): `entries.map` would throw here,
  // OUTSIDE the fetch try/catch, and reject instead of returning [] as documented.
  const raw = Array.isArray(data) ? data : (data && data.slideTypes)
  const entries = Array.isArray(raw) ? raw : []
  return entries.map(normalize).filter(Boolean)
}

export default { fetchDeveloperSlideTypes }
